<template>
<div>
  <div class="favourite-toggle__wrapper print-hide">

    <!--
      Toggled button:
      The content of the button has been defined
      mostly by currentButtonData() object.
    -->
    <button
      :class="[
        'favourite-toggle',
        'btn',
        toggled ? 'btn--toggled' : '',
        isButtonDisabled ? 'btn--visually-disabled' : '',
        status === 'Varattu' && this.type === 'owner-occupancy' ? 'favourite-toggle__reserved' : '',
        // Dynamic class by state.
        // this is usually setting color of the button. e.g. btn--pink
        currentButtonData.class
      ]"
      @click="toggle"
    >
      <span class="favourite-toggle__content">

        <span class="favourite-toggle__text">
          {{ currentButtonData.text }}
        </span>

        <!-- The owner-occupancy states ('join-the-queue', 'reserve-apartment') have no
        icon; rendering <component :is=""> would warn on every card. -->
        <component
          v-if="currentButtonData.icon"
          :is="currentButtonData.icon"
        />
      </span>
    </button>

    <!-- Remove from favourites button -->
    <button
      :class="[
        'favourite-toggle',
        'btn',
        `btn--pink`,
        ! toggled ? 'favourite-toggle--hidden' : '', // Visibility.
        toggled && this.type != 'owner-occupancy' ? 'application-btn--toggled' : ''
      ]"
      @click="remove()"
    >
        <span class="favourite-toggle__content">
          <span class="mr-1">
            {{translate('Poista hakemukselta')}}
          </span>

          <span>
            <TrashIcon />
          </span>

        </span>
    </button>

  </div>

    <!-- Exit modal -->
    <InfoModal
      v-if="showInfoModal"
    >
      <div v-html="modalContent"></div>

      <template v-slot:actions="slot">
        <button
          class="btn btn--large"
          :class="[`btn--light-green`, slot.classAction]"
          @click="showInfoModal = false"
        >
          {{translate('Sulje')}}
        </button>
      </template>
    </InfoModal>
  </div>
</template>

<script>
  import ApartmentAddedIcon from './icons/ApartmentAddedIcon.vue';
  import ApartmentAddIcon from './icons/ApartmentAddIcon.vue';
  import InfoModal from './components/InfoModal';
  import utils from './mixins/utils';
  import globals from '../lib/globals';
  import { useFavouritesStore } from './store/index';

  // Icons.
  import ChevronIcon from './icons/ChevronIcon.vue';
  import TrashIcon from './icons/TrashIcon.vue';
  import PlusIcon from './icons/PlusIcon.vue';

  export default {
    mixins: [
        utils.translate,
    ],
    props: {
        uuid: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        status: {
            type: String,
            required: true
        },
        canRemove: {
            type: Boolean,
            default: true,
        }
    },
    data() {
      return {
        showInfoModal: false,
      };
    },
    computed: {
        store() {
          return useFavouritesStore();
        },
        /**
         * Associative object to hold different cases button content: text, icon, CSS classes (color etc).
         */
        allButtonsData() {
          return {
                // Default currentButtonData()!
                'add-to-application': {
                  'text': this.translate( 'Lisää hakemukseen' ),
                  'icon': 'PlusIcon',
                  'class': 'btn--green-light',
                },
                'remove-btn': {
                  'text': this.translate( 'Poista hakemukselta' ),
                  'icon': 'TrashIcon',
                  'class': 'btn--pink',
                },
                'added-to-application': {
                  'text': this.translate( 'Lisätty hakemukselle' ),
                  'icon': 'ChevronIcon',
                  'class': 'btn--green-light',
                },
                'join-the-queue': {
                  'text': this.translate( 'Liity jonoon' ),
                  'icon': '',
                  'class': 'btn--peach',
                },
                'reserve-apartment': {
                  'text': this.translate( 'Varaa asunto' ),
                  'icon': '',
                  'class': 'btn--green-light',
                },
            };
        },
        /**
         * Handle the current card button's content.
         * If you add new case's button.
         * Add button properties in to the allButtonsData() object AND add logical conditions here that defines when the button should be visible.
         * Note the remove button is separate button just because of the readability.
         *
         * @return A matching object from allButtonsData().
         */
        currentButtonData() {

            // 'remove-btn'
            if (this.toggled && this.canRemove && this.type !== 'owner-occupancy') {
              return this.allButtonsData['remove-btn'];
            }

            // 'added-to-application'
            if (this.toggled && !this.canRemove && this.type !== 'owner-occupancy') {
              return this.allButtonsData['added-to-application'];
            }

            // 'add-to-application'
            if (!this.toggled && this.type !== 'owner-occupancy') {
              return this.allButtonsData['add-to-application'];
            }

            // 'join-the-queue'
            if (this.type === 'owner-occupancy' && this.status === 'Varattu') {
              return this.allButtonsData['join-the-queue'];
            }

            // 'reserve-apartment'
            if (this.type === 'owner-occupancy' && this.status !== 'Varattu') {
              return this.allButtonsData['reserve-apartment'];
            }

            // Fallback / initial state.
            return this.allButtonsData['add-to-application'];
        },
        toggled() {
          return this.store.isFavourite(this.uuid) && this.type !== "owner-occupancy";
        },
        /**
         * These links have been set on WordPress admin page "Nimetyt sivut".
         *
         * @returns string CTA link url by type.
         */
        ctaLink() {
          const ctaLink = {
                "right-of-occupancy": globals.urlApplicationRightOfOccupancy,
                "rental": globals.urlApplicationRent,
                "owner-occupancy": globals.urlApplicationOwnerOccupied,
            }[this.type];
            return ctaLink;
        },
        isButtonDisabled() {
          return !globals.userLoggedIn && globals.formDisabledFromWordPress;
        },
        modalContent() {
          return globals.formDisabledFromWordPressReason;
        },
    },
    methods: {
        remove() {
          const uuid = this.uuid;
          const type = this.type;

          this.store.removeFavourite({ uuid, type });
        },
        toggle() {
            const uuid = this.uuid;
            const type = this.type;

            // showInfoModal.
            if (
              ! globals.userLoggedIn &&
              globals.formDisabledFromWordPress &&
              this.type !== 'owner-occupancy'
            ) {
              this.showInfoModal = true;
              return false;
            }

            if (this.toggled && this.canRemove) {
                this.store.removeFavourite({ uuid, type });
            }
            else {
              this.store.emptyFavourites({ type: 'owner-occupancy' }); //not want to owner-occupancy in favourites
              this.store.addFavourite({ uuid, type });

              // This link has been set on WordPress admin page "Nimetyt sivut".
              if(type === 'owner-occupancy') {
                window.location.href = this.ctaLink;
              } else {
                if (this.canRemove) this.store.toggleFavouritesMenuExternal(true);
              }
            }
        },
    },
    components: {
      ApartmentAddedIcon,
      ApartmentAddIcon,
      InfoModal,
      ChevronIcon,
      TrashIcon,
      PlusIcon,
    }
}
</script>

<style lang="scss">
  @use "sass:math";
  @import "~styles/definitions";

  .favourite-toggle--hidden {
    display: none;
    opacity: 0;
    width: 0 !important;
    visibility: hidden;
    padding: 0;
  }

  .favourite-toggle {
    position: relative;
    width: 100%;
    height: 4rem;
    @include font-label-16;
    display: flex;
    font-weight: $font-weight-bold;

    &.btn-toggled {
      position: relative;

      &:before {
        content: ' ';
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: .25rem;
        background-color: $color-green-2025;
      }
    }
  }

  .favourite-toggle__wrapper {
    display: flex;
    flex-direction: row;
    position: relative;
    padding: $theme-spacing 0;
  }

  .btn--toggled {
    display: none;
    margin-top: 0;
  }

  .application-btn--toggled {
    opacity: 1;
    visibility: visible;
    width: 100%;
  }

  .favourite-toggle__content {
    display: flex;
    align-items: center;
    gap: math.div($theme-spacing, 2);
  }

  .favourite-toggle__text {
    @include font-label-16;
    display: flex;
    font-weight: $font-weight-bold;
  }

  .btn--visually-disabled {
    opacity: 0.75;
    color: rgba(255, 255, 255, 0.5);
  }

  .btn--left-border {
    position: relative;

    &:before {
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: .25rem;
      background-color: $color-green-2025;
    }
  }

</style>
